import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, UtensilsCrossed, Building2, Waves, Briefcase, ArrowLeft, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const THEME_COLOR = '#c59253';

const BusinessTypeSelection = ({ state, updateState, onNext }) => {
    const { t } = useTranslation();
    const [view, setView] = useState('main'); // 'main' or 'corporate'

    const mainTypes = [
        {
            id: 'restaurant',
            name: 'Restaurant',
            icon: UtensilsCrossed,
            description: 'Cafes, fine dining, or quick service outlets',
        },
        {
            id: 'corporate_trigger',
            name: 'Corporate & Services',
            icon: Briefcase,
            description: 'Offices, beach clubs, or residential services',
            isLocked: true
        }
    ];

    const corporateSubTypes = [
        {
            id: 'office',
            name: 'Office / Corporate',
            icon: Building2,
            description: 'Corporate hubs and office-based services',
        },
        {
            id: 'beach',
            name: 'Beach / Pool',
            icon: Waves,
            description: 'Beach clubs, pool bars, and outdoor venues',
        }
    ];

    const handleSelectMainType = (id) => {
        if (id === 'corporate_trigger') {
            setView('corporate');
        } else {
            updateState({ businessType: id });
            setTimeout(() => onNext(), 300);
        }
    };

    const handleSelectSubType = (id) => {
        updateState({ businessType: id });
        setTimeout(() => onNext(), 300);
    };

    const currentTypes = view === 'main' ? mainTypes : corporateSubTypes;

    return (
        <div className="h-100 overflow-auto bg-light">
            <div className="container py-5">
                <div className="text-center mb-5 position-relative">
                    {view === 'corporate' && (
                        <button
                            onClick={() => setView('main')}
                            className="btn btn-link position-absolute start-0 top-0 p-0 text-muted d-flex align-items-center gap-2 text-decoration-none"
                            style={{ transition: 'all 0.2s' }}
                        >
                            <ArrowLeft size={20} />
                            <span className="d-none d-sm-inline fw-bold small">{t('Back')}</span>
                        </button>
                    )}
                    <h1 className="mb-2 fw-bold" style={{ letterSpacing: '-0.5px' }}>
                        {view === 'main' ? t('What type of business are you?') : t('Refine your business type')}
                    </h1>
                    <p className="text-muted mx-auto small" style={{ maxWidth: '500px' }}>
                        {view === 'main'
                            ? t('Select your primary category to tailor your dashboard experience')
                            : t('Choose the specific environment that matches your operations')}
                    </p>
                </div>

                <div className="row g-3 justify-content-center px-2">
                    <AnimatePresence mode="wait">
                        {currentTypes.map((type) => {
                            const Icon = type.icon;
                            return (
                                <motion.div
                                    key={type.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                    className="col-12 col-sm-6 col-md-5"
                                >
                                    <button
                                        onClick={() => {
                                            if (type.isLocked) return;
                                            view === 'main' ? handleSelectMainType(type.id) : handleSelectSubType(type.id);
                                        }}
                                        disabled={type.isLocked}
                                        className={`w-100 text-start position-relative bg-white rounded-4 p-4 border-0 h-100 d-flex flex-column align-items-center align-items-sm-start text-center text-sm-start ${type.isLocked ? 'opacity-75' : ''}`}
                                        style={{
                                            boxShadow: '0 4px 15px -3px rgba(0, 0, 0, 0.05), 0 2px 6px -2px rgba(0, 0, 0, 0.05)',
                                            transition: 'all 0.2s ease',
                                            border: '2px solid transparent',
                                            cursor: type.isLocked ? 'not-allowed' : 'pointer',
                                            filter: type.isLocked ? 'grayscale(0.5)' : 'none'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!type.isLocked) e.currentTarget.style.borderColor = THEME_COLOR;
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!type.isLocked) e.currentTarget.style.borderColor = 'transparent';
                                        }}
                                    >
                                        {type.isLocked && (
                                            <div className="position-absolute" style={{ top: '1.25rem', right: '1.25rem' }}>
                                                <Lock size={18} className="text-muted opacity-50" />
                                            </div>
                                        )}

                                        <div className="d-flex align-items-center justify-content-center rounded-circle mb-3 shadow-sm bg-light" style={{ width: '56px', height: '56px' }}>
                                            <Icon size={24} style={{ color: type.isLocked ? '#adb5bd' : '#212529' }} />
                                        </div>

                                        <h3 className="h5 fw-bold mb-2">{t(type.name)}</h3>
                                        <p className="text-muted mb-0 small" style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                                            {t(type.description)}
                                        </p>

                                        {type.isLocked && (
                                            <div className="mt-auto pt-3">
                                                <p className="mb-0 text-muted fw-light" style={{ fontSize: '0.75rem' }}>
                                                    {t('To enable this, contact admin ')}
                                                    <a href='mailto:contact@clickorder.io' className="fw-medium text-dark">contact@clickorder.io</a>
                                                </p>
                                            </div>
                                        )}

                                        {state.businessType === type.id && (
                                            <div
                                                className="position-absolute d-flex align-items-center justify-content-center rounded-circle shadow-sm"
                                                style={{
                                                    top: '1rem',
                                                    right: '1rem',
                                                    width: '24px',
                                                    height: '24px',
                                                    backgroundColor: THEME_COLOR
                                                }}
                                            >
                                                <Check size={16} className="text-white" />
                                            </div>
                                        )}
                                    </button>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default BusinessTypeSelection;
